import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import useTokenStore from "../../stores/tokens";
import { compatApi } from "../../utils/Intercepter";
import useValidationStore from "../../stores/emailValid";
import dayjs from "dayjs";
import { PURPLE, hexToRgba } from "../../utils/designUtils";

export default function EmailVerified() {
  const userName = useTokenStore((state) => state.user?.userName);
  const userEmail = useTokenStore((state) => state.user?.userEmail);
  const setUser = useTokenStore((state) => state.setUser);
  const { emailSent } = useValidationStore();

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [expiredMessage, setExpiredMessage] = useState('');
  const inputRefs = useRef([]);

  const toastConfig = {
    position: "bottom-right", autoClose: 2800,
    pauseOnHover: false, pauseOnFocusLoss: false, draggable: false,
  };

  useEffect(() => {
    const { accessToken, user } = useTokenStore.getState();
    const emailSent = useValidationStore.getState().emailSent;
    if (!accessToken || !user?.userEmail || emailSent) return;

    const checkVerified = async () => {
      try {
        const response = await compatApi.get('/email/check');
        if (response.ok) {
          const { expireAt } = response.data;
          setExpiredMessage(`${dayjs(expireAt).format('YYYY-MM-DD HH:mm')} 까지 인증코드를 입력해주세요.`);
        }
      } catch (error) {
        const { message, httpStatus } = error.response?.data || {};
        if (httpStatus === 400) {
          useValidationStore.getState().setEmailSent(true);
          setExpiredMessage(`${dayjs().add(5, 'minute').format('YYYY-MM-DD HH:mm')} 까지 인증코드를 입력해주세요.`);
          sendVerificationEmail();
        } else {
          toast.error(message || '오류가 발생했습니다.', toastConfig);
        }
      }
    };

    const sendVerificationEmail = async () => {
      const toastId = toast.info('이메일 인증 요청중...', toastConfig);
      try {
        const response = await compatApi.get('/email/send');
        if (response.ok) {
          const { expireAt } = response.data;
          toast.dismiss(toastId);
          toast.success('인증 코드가 이메일로 전송되었습니다.', toastConfig);
          setExpiredMessage(`${dayjs(expireAt).format('YYYY-MM-DD HH:mm')} 까지 인증코드를 입력해주세요.`);
        } else {
          toast.dismiss(toastId);
          toast.error('이메일 전송에 실패했습니다.', toastConfig);
        }
      } catch (error) {
        const errorMessage = error.response?.data;
        toast.error(errorMessage || '인증 중 오류가 발생했습니다.', toastConfig);
      }
    };
    checkVerified();
  }, [emailSent]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (index, value) => {
    const sanitizedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (sanitizedValue.length > 1) {
      const chars = sanitizedValue.split('').slice(0, 6);
      const newCode = [...code];
      chars.forEach((char, i) => { if (index + i < 6) newCode[index + i] = char; });
      setCode(newCode);
      const lastIndex = Math.min(index + chars.length - 1, 5);
      inputRefs.current[lastIndex]?.focus();
      return;
    }
    const newCode = [...code];
    newCode[index] = sanitizedValue;
    setCode(newCode);
    if (sanitizedValue && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      }
    }
  };

  const handleSubmit = async () => {
    const verificationCode = code.join('');
    if (verificationCode.length !== 6) {
      toast.error('6자리 인증 코드를 모두 입력해주세요.', toastConfig);
      return;
    }
    try {
      const response = await compatApi.post('/email/verify', { verifyCode: verificationCode });
      if (response.ok) {
        toast.success('이메일 인증이 완료되었습니다!', toastConfig);
        setUser({ isEmailVerified: true });
      } else {
        toast.error(response.data || '인증에 실패했습니다.', toastConfig);
      }
    } catch (error) {
      const errorMessage = error.response?.data;
      toast.error(errorMessage || '인증 중 오류가 발생했습니다.', toastConfig);
    }
  };

  const handleResend = async () => {
    const toastId = toast.info('이메일 재전송 요청중...', toastConfig);
    try {
      const response = await compatApi.get('/email/send');
      toast.dismiss(toastId);
      if (response.ok) {
        toast.success('인증 코드가 재전송되었습니다.', toastConfig);
      } else {
        const { message } = response.data || {};
        toast.error(message || '이메일 재전송에 실패했습니다.', toastConfig);
      }
    } catch (error) {
      toast.dismiss(toastId);
      const { message } = error.response?.data || {};
      toast.error(message || '오류가 발생했습니다. 잠시후 다시 시도해주세요.', toastConfig);
    }
  };

  return (
    <div style={{ background: '#f5f4fa', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Welcome header */}
      <div style={{
        background: 'white', borderBottom: '1px solid #f0eff5',
        padding: '20px clamp(20px, 3vw, 40px)',
      }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1f1d2e' }}>
          환영합니다, {userName}님!
        </h1>
      </div>

      <main style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '32px 16px',
      }}>
        <div style={{
          background: 'white', borderRadius: 16,
          border: '1px solid rgba(124,107,217,0.12)',
          boxShadow: '0 4px 24px rgba(124,107,217,0.10)',
          padding: '40px', width: '100%', maxWidth: 480,
        }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#1f1d2e' }}>
            이메일 인증이 필요합니다.
          </h2>
          <p style={{ margin: '0 0 24px', fontSize: 13.5, color: '#7a7989', lineHeight: 1.6 }}>
            {`${userEmail} 로 인증코드를 보냈습니다.`}<br/>
            {expiredMessage}
          </p>

          {/* 6-box OTP input */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 28 }}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => {
                  const paste = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '');
                  if (paste.length === 6) {
                    setCode(paste.split(''));
                    inputRefs.current[5]?.focus();
                    e.preventDefault();
                  }
                }}
                style={{
                  width: 52, height: 56, fontSize: 24, fontWeight: 700,
                  textAlign: 'center', textTransform: 'uppercase',
                  border: `2px solid ${digit ? PURPLE : '#e5e0f7'}`,
                  borderRadius: 10, outline: 'none',
                  background: digit ? hexToRgba(PURPLE, 0.06) : '#faf9fe',
                  color: '#1f1d2e', fontFamily: 'inherit',
                  boxSizing: 'border-box', boxShadow: 'none',
                  transition: 'border-color 150ms, background 150ms',
                }}
                onFocus={e => e.target.style.borderColor = PURPLE}
                onBlur={e => e.target.style.borderColor = digit ? PURPLE : '#e5e0f7'}
              />
            ))}
          </div>

          {/* Resend + Submit row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: '#7a7989', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>인증 코드를 받지 못하셨나요?</span>
              <button onClick={handleResend} style={{
                border: 'none', background: 'none', padding: 0,
                color: PURPLE, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit',
              }}>재전송</button>
            </div>
            <button onClick={handleSubmit} style={{
              height: 40, padding: '0 22px', borderRadius: 10,
              border: 'none',
              background: `linear-gradient(135deg, ${PURPLE}, #9b8de8)`,
              color: 'white', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>확인</button>
          </div>
        </div>
      </main>
    </div>
  );
}
